'use client'

import { ReactNode } from 'react'
import { ArrowDown } from 'lucide-react'
import {
    Block,
    FeatureGrid,
    Head,
    OtherSolutions,
    SolutionCase,
    SolutionCta,
    SolutionHero,
    SolutionPage,
    VerticalFlow,
} from '@/components/platform/v2/SolutionKit'
import { PhoneMock, MockRow } from '@/components/platform/v2/Mocks'
import { SolutionMeta } from '@/data/solutions'

interface Feature {
    title: string
    text?: string
}

export interface BusinessSolutionConfig {
    meta: SolutionMeta
    eyebrow: string
    titleLines: string[]
    lead: string
    problems: Feature[]
    flow: string[]
    flowTitle: string
    flowNote?: string
    features: Feature[]
    featuresTitle: string
    audience?: string[]
    visual?: ReactNode
    thesis?: string
    extra?: ReactNode
    caseTitle?: string
}

const DefaultVisual = ({ steps }: { steps: string[] }) => (
    <div className="mx-auto w-full max-w-md rounded-3xl border border-border/60 bg-[hsl(var(--charcoal))] p-5 shadow-2xl">
        <p className="eyebrow text-center text-primary">Единый процесс</p>
        <div className="mt-4 flex flex-col gap-2">
            {steps.slice(0, 5).map((step, index) => (
                <div key={step} className="flex flex-col items-center gap-2">
                    <div className="w-full rounded-xl border border-border/50 bg-[hsl(var(--deep-black))] px-4 py-3 text-center text-xs font-bold text-foreground">
                        {step}
                    </div>
                    {index < Math.min(steps.length, 5) - 1 && <ArrowDown className="h-3.5 w-3.5 text-primary" />}
                </div>
            ))}
        </div>
    </div>
)

export const CommerceAppVisual = () => (
    <PhoneMock title="Приложение магазина">
        {[
            ['Покупки', 'история'],
            ['Избранное', 'сохранено'],
            ['Баллы', 'начислены'],
            ['Предложения', 'для вас'],
            ['Повторить заказ', 'в один шаг'],
            ['Чат', 'онлайн'],
        ].map(([label, value], index) => (
            <MockRow key={label} label={label} value={value} accent={index === 4} />
        ))}
    </PhoneMock>
)

const BusinessSolutionPage = ({ config }: { config: BusinessSolutionConfig }) => {
    const { meta } = config
    const scenario = meta.path.split('/').pop() ?? meta.id

    return (
        <SolutionPage meta={meta}>
            <SolutionHero
                meta={meta}
                eyebrow={config.eyebrow}
                titleLines={config.titleLines}
                lead={config.lead}
                primaryLabel="Собрать решение для моего бизнеса"
                primaryTo={`/signin?scenario=${scenario}`}
                audience={config.audience}
                visual={config.visual ?? <DefaultVisual steps={config.flow} />}
            />

            <Block tone="quiet">
                <Head eyebrow="Что обычно происходит" title="Разрозненные инструменты разрывают путь клиента" align="center" />
                <div className="mt-10">
                    <FeatureGrid items={config.problems} columns={2} />
                </div>
            </Block>

            <Block>
                <Head eyebrow="Как работает VCENTRE.ONE" title={config.flowTitle} align="center" text={config.flowNote} />
                <div className="mt-10">
                    <VerticalFlow steps={config.flow} />
                </div>
            </Block>

            {config.thesis && (
                <Block tone="quiet">
                    <p className="mx-auto max-w-4xl text-center text-2xl font-extrabold leading-tight text-foreground sm:text-4xl">
                        {config.thesis}
                    </p>
                </Block>
            )}

            <Block>
                <Head eyebrow="Возможности" title={config.featuresTitle} align="center" />
                <div className="mt-10">
                    <FeatureGrid items={config.features} />
                </div>
            </Block>

            {config.extra}

            <Block tone="quiet">
                <SolutionCase slugs={meta.caseSlugs} title={config.caseTitle ?? 'Проекты с похожим клиентским путём'} />
            </Block>

            <Block>
                <OtherSolutions meta={meta} />
            </Block>

            <Block tone="quiet">
                <SolutionCta
                    title="Покажите, как сейчас работает ваш бизнес"
                    text="Получите концепт приложения, клиентского пути и рабочего контура."
                    buttonLabel="Собрать решение"
                    scenario={scenario}
                    to={`/signin?scenario=${scenario}&source=solution-bottom`}
                    visual={
                        <img
                            src={'/assets/images/platform/solutions-mascot-cta.png'}
                            alt="Утёнок VCENTRE с ракетой и ноутбуком — Запускаем быстро"
                            className="mx-auto w-full max-w-[280px] object-contain sm:max-w-[340px]"
                        />
                    }
                />
            </Block>
        </SolutionPage>
    )
}

export default BusinessSolutionPage
